import { useState, useEffect, useCallback } from 'react'; // Removed useRef
import { BarcodeType2, BarcodeType2ListResponse, BarcodeType2Create } from '../types/costwand';
import { apiClient, ApiError } from '../api/apiClient.ts';

const PAGE_SIZE = 10;

interface UseBarcodeType2Result {
  BarcodeType2: BarcodeType2[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number; // Added totalItems to the result interface
  fetchBarcodeType2: (page: number) => Promise<void>; // Changed to required page argument
  handlePageChange: (page: number) => void;
  createBarcodeType2: (data: BarcodeType2Create) => Promise<boolean>;
  updateBarcodeType2: (id: number, data: BarcodeType2Create) => Promise<boolean>;
  deleteBarcodeType2: (id: number) => Promise<boolean>;
}

export const useBarcodeType2 = (): UseBarcodeType2Result => {
  const [BarcodeType2, setBarcodeType2] = useState<BarcodeType2[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0); // New state for totalItems
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBarcodeType2 = useCallback(async (page: number) => { // Made page a required argument
    if (page < 1) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient<BarcodeType2ListResponse>(
        `/barcode_2?page=${page}&page_size=${PAGE_SIZE}`,
        { bypassCache: true } // ADDED: Bypass cache for pagination
      );
      setBarcodeType2(response.data);
      setTotalPages(response.pagination.total_pages);
      setTotalItems(response.pagination.total_items); // Set totalItems from response
      // REMOVED: setCurrentPage(response.pagination.page);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Failed to fetch Barcode Type 2s: ${err.message}`);
      } else {
        setError('An unknown error occurred while fetching Barcode Type 2s.');
      }
      setBarcodeType2([]);
      setTotalItems(0); // Reset totalItems on error
    } finally {
      setLoading(false);
    }
  }, []); // Removed currentPage from dependencies

  useEffect(() => {
    // Simplified useEffect: always fetch when currentPage changes
    fetchBarcodeType2(currentPage);
  }, [currentPage, fetchBarcodeType2]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const createBarcodeType2 = async (data: BarcodeType2Create): Promise<boolean> => {
    setError(null);
    try {
      await apiClient<BarcodeType2>('/barcode_2', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      //alert('Barcode Type 2 created successfully!');
      await fetchBarcodeType2(1); // Trigger refetch via useEffect // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      //setError(err instanceof ApiError ? `Failed to create Barcode Type 2: ${err.message}` : 'An unknown error occurred during creation.');
      return false;
    }
  };

  const updateBarcodeType2 = async (id: number, data: BarcodeType2Create): Promise<boolean> => {
    setError(null);
    try {
      await apiClient<BarcodeType2>(`/barcode_2/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      //alert('Barcode Type 2 updated successfully!');
      await fetchBarcodeType2(currentPage); // Re-fetch current page to show updated data // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      //setError(err instanceof ApiError ? `Failed to update Barcode Type 2: ${err.message}` : 'An unknown error occurred during update.');
      return false;
    }
  };

  const deleteBarcodeType2 = async (id: number): Promise<boolean> => {
    if (!window.confirm(`Are you sure you want to delete Barcode Type 2 ID: ${id}?`)) return false;
    setError(null);
    try {
      await apiClient<void>(`/barcode_2/${id}`, { method: 'DELETE' });
      //alert(`Barcode Type 2 ${id} deleted successfully!`);
      const pageToFetch = BarcodeType2.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      await fetchBarcodeType2(pageToFetch); // Trigger refetch via useEffect // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      ////setError(err instanceof ApiError ? `Failed to delete Barcode Type 2: ${err.message}` : 'An unknown error occurred during deletion.');
      return false;
    }
  };

  return {
    BarcodeType2,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems, // Return totalItems
    fetchBarcodeType2,
    handlePageChange,
    createBarcodeType2,
    updateBarcodeType2,
    deleteBarcodeType2,
  };
};